// Exercise visual guides and machine information for Planet Fitness

export const exerciseVisuals = {
  // Planet Fitness Machines
  "Leg Press": {
    machineType: "Leg Press Machine",
    howToFind: "Large angled seat with a platform for your feet. Usually in the corner or against a wall. Has a weighted sled that moves up and down at an angle.",
    visualDescription: "You'll see a big angled seat (45 degrees) with a large square platform above it where you place your feet. The platform is connected to weight plates or a pin-loaded weight stack.",
    setup: [
      "Sit in the angled seat with your back flat against the pad",
      "Place feet shoulder-width apart on the platform",
      "Feet should be centered on the platform",
      "Release the safety handles on each side",
      "Lower the platform until knees are at 90 degrees",
      "Push through heels to extend legs"
    ],
    targetMuscles: ["Quadriceps", "Glutes", "Hamstrings", "Calves"],
    commonMistakes: [
      "Knees caving inward - keep them aligned with toes",
      "Pushing through toes instead of heels",
      "Locking knees at the top",
      "Going too heavy too fast"
    ],
    imageUrl: "https://images.unsplash.com/photo-1584380931214-dbb5b72e93f0?w=400",
    videoTutorial: "Search YouTube: 'How to use leg press machine'"
  },

  "Chest Press": {
    machineType: "Chest Press Machine",
    howToFind: "Looks like a bench with two handles at chest level. You push forward. Usually near other upper body machines.",
    visualDescription: "Upright seat with a backrest. Two handles/grips at chest height that you push away from you. Has a weight stack with a pin to select weight.",
    setup: [
      "Adjust seat height so handles align with mid-chest",
      "Sit with back flat against pad",
      "Grip handles with hands slightly wider than shoulders",
      "Feet flat on floor",
      "Push handles forward until arms nearly straight",
      "Control the return - don't let it slam back"
    ],
    targetMuscles: ["Pectorals (Chest)", "Anterior Deltoids (Front Shoulders)", "Triceps"],
    commonMistakes: [
      "Seat too high or too low - handles should be mid-chest",
      "Arching back off the pad",
      "Not using full range of motion",
      "Letting shoulders shrug up"
    ],
    imageUrl: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400",
    videoTutorial: "Search YouTube: 'How to use chest press machine'"
  },

  "Shoulder Press": {
    machineType: "Shoulder Press Machine",
    howToFind: "Upright seat with handles starting at shoulder height. You push straight up overhead.",
    visualDescription: "Vertical seat with backrest. Two handles at shoulder level that move straight up. Pin-loaded weight stack behind or beside the seat.",
    setup: [
      "Adjust seat so handles are at shoulder height when seated",
      "Sit upright with core engaged",
      "Grip handles with palms facing forward",
      "Press straight up until arms are nearly locked",
      "Lower with control back to shoulder height"
    ],
    targetMuscles: ["Deltoids (All 3 heads)", "Trapezius", "Triceps"],
    commonMistakes: [
      "Arching lower back - keep core tight",
      "Not pressing straight up",
      "Locking elbows too hard at top",
      "Using momentum instead of muscle"
    ],
    imageUrl: "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?w=400",
    videoTutorial: "Search YouTube: 'How to use shoulder press machine'"
  },

  "Seated Cable Row": {
    machineType: "Seated Row Machine / Cable Row",
    howToFind: "Seat facing a cable pulley at chest height. Has foot plates in front. You pull the handle to your torso.",
    visualDescription: "Bench or seat facing a cable machine. Foot rests in front. Handle attached to cable at chest level. You sit and pull toward your stomach.",
    setup: [
      "Sit on bench with feet on foot plates",
      "Slight bend in knees",
      "Grab handle with both hands",
      "Sit upright with chest out",
      "Pull handle to lower chest/stomach",
      "Squeeze shoulder blades together at the end"
    ],
    targetMuscles: ["Latissimus Dorsi", "Rhomboids", "Trapezius", "Rear Deltoids", "Biceps"],
    commonMistakes: [
      "Rocking back and forth - stay upright",
      "Not squeezing shoulder blades together",
      "Pulling with arms instead of back",
      "Shrugging shoulders up"
    ],
    imageUrl: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400",
    videoTutorial: "Search YouTube: 'How to use seated cable row'"
  },

  "Lateral Raise Machine": {
    machineType: "Lateral Raise Machine",
    howToFind: "Seat with pads on the outside of your upper arms. You push your arms out to the sides.",
    visualDescription: "Upright seat with padded arms/levers on each side. The pads rest against your upper arms/elbows. You push outward.",
    setup: [
      "Adjust seat so pads hit mid-upper arm",
      "Sit with back against pad",
      "Place upper arms against the pads",
      "Push arms out and up to shoulder height",
      "Lead with elbows, not hands",
      "Lower with control"
    ],
    targetMuscles: ["Lateral Deltoids (Side Shoulders)", "Upper Trapezius"],
    commonMistakes: [
      "Going too heavy - this is isolation work",
      "Shrugging shoulders up",
      "Raising above shoulder height",
      "Using momentum"
    ],
    imageUrl: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400",
    videoTutorial: "Search YouTube: 'How to use lateral raise machine'"
  },

  "Assisted Pull-up Machine": {
    machineType: "Assisted Pull-up/Dip Machine",
    howToFind: "Platform to kneel on with pull-up bars above. The platform moves up to help you. More weight = more assistance.",
    visualDescription: "Tall machine with pull-up bars at the top and a platform/pad to kneel on at the bottom. Weight stack provides assistance (more weight = easier).",
    setup: [
      "Set assistance weight (start high: 80-100 lbs)",
      "Step onto the machine and grip pull-up bars",
      "Kneel on the platform",
      "Pull yourself up until chin is over bar",
      "Lower with control",
      "Remember: MORE weight = MORE help"
    ],
    targetMuscles: ["Latissimus Dorsi", "Biceps", "Upper Back", "Core"],
    commonMistakes: [
      "Using too little assistance - start high!",
      "Not pulling chin over bar",
      "Kipping/swinging - keep it strict",
      "Forgetting to engage core"
    ],
    imageUrl: "https://images.unsplash.com/photo-1534367990352-fbe80f398b9f?w=400",
    videoTutorial: "Search YouTube: 'How to use assisted pull-up machine'"
  },

  // Bodyweight Exercises
  "Push-ups": {
    machineType: "Bodyweight Exercise",
    howToFind: "No equipment needed - just floor space",
    visualDescription: "Classic push-up position: hands on floor, body straight from head to heels",
    setup: [
      "Start in plank position",
      "Hands slightly wider than shoulders",
      "Body in straight line from head to heels",
      "Lower chest to ground",
      "Keep elbows at 45-degree angle (not flared out)",
      "Push back up to start"
    ],
    targetMuscles: ["Pectorals", "Anterior Deltoids", "Triceps", "Core"],
    variations: [
      "Knee push-ups (easier)",
      "Decline push-ups (feet elevated - harder)",
      "Diamond push-ups (hands together - triceps focus)",
      "Wide push-ups (chest focus)"
    ],
    commonMistakes: [
      "Hips sagging - keep core tight",
      "Not going low enough",
      "Elbows flaring out too much",
      "Looking up - keep neutral neck"
    ],
    imageUrl: "https://images.unsplash.com/photo-1598971639058-fab3c3109a00?w=400",
    videoTutorial: "Search YouTube: 'Perfect push-up form'"
  },

  "Pull-ups": {
    machineType: "Pull-up Bar",
    howToFind: "Horizontal bar mounted high (usually at doorway height or in squat rack)",
    visualDescription: "High horizontal bar. You hang and pull yourself up until chin clears the bar.",
    setup: [
      "Grip bar with hands shoulder-width apart",
      "Palms facing away (overhand grip)",
      "Hang with arms fully extended",
      "Pull yourself up by engaging back",
      "Get chin over the bar",
      "Lower with control"
    ],
    targetMuscles: ["Latissimus Dorsi", "Biceps", "Upper Back", "Core", "Forearms"],
    variations: [
      "Band-assisted pull-ups",
      "Negative pull-ups (jump up, slow down)",
      "Chin-ups (palms facing you)",
      "Wide-grip pull-ups"
    ],
    progressionTips: [
      "Start with negatives (3-5 second descent)",
      "Use resistance bands for assistance",
      "Dead hangs to build grip strength",
      "Aim for 1 more rep each week"
    ],
    commonMistakes: [
      "Kipping/swinging",
      "Not full range of motion",
      "Not engaging back muscles",
      "Shrugging shoulders"
    ],
    imageUrl: "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?w=400",
    videoTutorial: "Search YouTube: 'How to do pull-ups for beginners'"
  },

  "Pike Push-ups": {
    machineType: "Bodyweight Exercise",
    howToFind: "No equipment needed - just floor space",
    visualDescription: "Downward dog yoga position, then lower head toward ground",
    setup: [
      "Start in downward dog position",
      "Hips high in the air",
      "Head between your arms",
      "Hands shoulder-width apart",
      "Lower head toward ground",
      "Push back up"
    ],
    targetMuscles: ["Anterior Deltoids", "Upper Pectorals", "Triceps"],
    variations: [
      "Feet on floor (easier)",
      "Feet on chair (harder - more shoulder focus)",
      "Handstand push-ups (advanced)"
    ],
    progressionTips: [
      "Master regular pike push-ups first",
      "Elevate feet for more difficulty",
      "Work toward wall-assisted handstand push-ups"
    ],
    commonMistakes: [
      "Not keeping hips high enough",
      "Going too deep too fast",
      "Not controlling the descent"
    ],
    imageUrl: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400",
    videoTutorial: "Search YouTube: 'How to do pike push-ups'"
  },

  "Tricep Dips": {
    machineType: "Chair, Bench, or Parallel Bars",
    howToFind: "Any sturdy chair, bench, or parallel dip bars at the gym",
    visualDescription: "Hands behind you on bench/chair, legs extended forward, lower and raise body",
    setup: [
      "Sit on edge of bench/chair",
      "Place hands next to hips",
      "Slide butt off the bench",
      "Legs extended (or bent for easier)",
      "Lower body by bending elbows",
      "Keep elbows close to body",
      "Push back up"
    ],
    targetMuscles: ["Triceps", "Chest", "Anterior Deltoids"],
    variations: [
      "Bent knees (easier)",
      "Straight legs (harder)",
      "Feet elevated (hardest)",
      "Weighted (add plate on lap)"
    ],
    commonMistakes: [
      "Elbows flaring out wide",
      "Going too low (past 90 degrees)",
      "Shoulders rolling forward"
    ],
    imageUrl: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400",
    videoTutorial: "Search YouTube: 'How to do tricep bench dips'"
  },

  "Romanian Deadlifts": {
    machineType: "Dumbbells",
    howToFind: "Dumbbell rack at Planet Fitness",
    visualDescription: "Hold dumbbells at sides, hinge at hips, lower weights down legs, feel hamstring stretch",
    setup: [
      "Hold dumbbells at sides (start 25-30 lbs each)",
      "Feet hip-width apart",
      "Slight bend in knees",
      "Hinge at hips (push butt back)",
      "Lower dumbbells down front of legs",
      "Feel stretch in hamstrings",
      "Stand back up by squeezing glutes"
    ],
    targetMuscles: ["Hamstrings", "Glutes", "Lower Back", "Erector Spinae"],
    commonMistakes: [
      "Squatting instead of hinging",
      "Rounding back - keep spine neutral",
      "Not feeling hamstring stretch",
      "Bending knees too much"
    ],
    imageUrl: "https://images.unsplash.com/photo-1532384748853-8f54a8f476e2?w=400",
    videoTutorial: "Search YouTube: 'How to do dumbbell Romanian deadlifts'"
  },

  "Goblet Squats": {
    machineType: "Dumbbell",
    howToFind: "Single dumbbell from rack",
    visualDescription: "Hold one dumbbell vertically at chest like a goblet, squat deep",
    setup: [
      "Hold one dumbbell vertically at chest",
      "Cup the top weight with both hands",
      "Feet slightly wider than shoulder-width",
      "Toes slightly pointed out",
      "Squat down keeping chest up",
      "Go as deep as comfortable",
      "Drive through heels to stand"
    ],
    targetMuscles: ["Quadriceps", "Glutes", "Core", "Upper Back"],
    commonMistakes: [
      "Knees caving in",
      "Heels lifting off ground",
      "Leaning forward too much",
      "Not squatting deep enough"
    ],
    imageUrl: "https://images.unsplash.com/photo-1574680178050-55c6a6a96e0a?w=400",
    videoTutorial: "Search YouTube: 'How to do goblet squats'"
  }
};

// Helper function to get visual info for an exercise
export const getExerciseVisual = (exerciseName) => {
  // Try exact match first
  if (exerciseVisuals[exerciseName]) {
    return exerciseVisuals[exerciseName];
  }

  // Try partial match (for exercises with variations in parentheses)
  for (const [key, value] of Object.entries(exerciseVisuals)) {
    if (exerciseName.includes(key) || key.includes(exerciseName)) {
      return value;
    }
  }

  return null;
};
